import type { ChatSuggestionsPayload, SuggestionChipState } from '@/types';

export interface SuggestionStreamHandlers {
  defaultSlotCount: number;
  emptySlots: (count: number) => SuggestionChipState[];
  upsertInThread: (
    runId: string,
    chips: SuggestionChipState[],
    cursor?: ChatSuggestionsPayload['cursor'],
    finalized?: boolean,
  ) => void;
  applyDelta: (runId: string, chipIndex: number, field: 'label' | 'message', text: string) => void;
  applyReady: (
    runId: string,
    chipIndex: number,
    chip: { id: string; label: string; message: string },
  ) => void;
  patchStopCursor: (
    runId: string,
    cursor: ChatSuggestionsPayload['cursor'],
    suggestions: ChatSuggestionsPayload['suggestions'],
  ) => void;
  recordSatisfiedCursor: (payload: ChatSuggestionsPayload) => void;
  hasProgressiveChips: (runId: string) => boolean;
  clearInThread: (runId: string) => void;
}

const SUGGESTION_EVENT_TYPES = new Set([
  'suggestion_block_start',
  'suggestion_delta',
  'suggestion_chip_ready',
  'suggestion_block_stop',
]);

export function isSuggestionStreamEvent(eventType: string): boolean {
  return SUGGESTION_EVENT_TYPES.has(eventType);
}

/** Apply one suggestion SSE event. Returns true if handled. */
export function applySuggestionStreamEvent(
  eventType: string,
  data: Record<string, unknown>,
  handlers: SuggestionStreamHandlers,
): boolean {
  switch (eventType) {
    case 'suggestion_block_start':
      if (data.runId) {
        handlers.upsertInThread(
          String(data.runId),
          handlers.emptySlots(handlers.defaultSlotCount),
        );
      }
      return true;

    case 'suggestion_delta':
      if (data.runId && typeof data.chip_index === 'number' && data.field && data.text) {
        handlers.applyDelta(
          String(data.runId),
          data.chip_index,
          data.field === 'message' ? 'message' : 'label',
          String(data.text),
        );
      }
      return true;

    case 'suggestion_chip_ready':
      if (data.runId && typeof data.chip_index === 'number' && data.chip) {
        handlers.applyReady(String(data.runId), data.chip_index, data.chip as {
          id: string;
          label: string;
          message: string;
        });
      }
      return true;

    case 'suggestion_block_stop':
      if (data.runId) {
        const runId = String(data.runId);
        const cursor = data.cursor as ChatSuggestionsPayload['cursor'] | undefined;
        const suggestions = (data.suggestions || []) as ChatSuggestionsPayload['suggestions'];
        if (handlers.hasProgressiveChips(runId)) {
          handlers.patchStopCursor(runId, cursor, suggestions);
        } else if (Array.isArray(suggestions) && suggestions.length) {
          const chips: SuggestionChipState[] = suggestions.map((s) => ({
            id: s.id,
            label: s.label,
            message: s.message,
            labelComplete: true,
            messageComplete: true,
            clickable: true,
          }));
          handlers.upsertInThread(runId, chips, cursor, true);
        } else {
          handlers.clearInThread(runId);
        }
        if (cursor) {
          handlers.recordSatisfiedCursor({ suggestions, cursor });
        }
      }
      return true;

    default:
      return false;
  }
}
